import { zipSync, strToU8 } from 'fflate';
import { websiteSchema, type Website } from './ai';

// Export generated code as data. It is never executed on the host.
export function websiteFiles(input: unknown): Record<string, Uint8Array> {
  const site = websiteSchema.parse(input);
  let html = site.html;
  if (!/<html[\s>]/i.test(html))
    html = `<!doctype html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body>${html}</body></html>`;
  html = html
    .replace(/<\/head>/i, '<link rel="stylesheet" href="styles.css"></head>')
    .replace(/<\/body>/i, '<script src="script.js"></script></body>');
  if (!/<head[\s>]/i.test(html))
    html = html.replace(
      /<html[^>]*>/i,
      (match) => match + '<head><link rel="stylesheet" href="styles.css"></head>',
    );
  const files: Record<string, Uint8Array> = {
    'index.html': strToU8(html),
    'styles.css': strToU8(site.css),
    'script.js': strToU8(site.js),
    'assets/README.txt': strToU8(
      'Generated assets are stored in this folder. External URLs in older websites remain external dependencies.',
    ),
  };
  const paths = new Set(Object.keys(files).map((path) => path.toLowerCase()));
  for (const asset of site.assets) {
    if (paths.has(asset.path.toLowerCase())) throw Error('Duplicate asset path.');
    paths.add(asset.path.toLowerCase());
    files[asset.path] =
      asset.encoding === 'utf8'
        ? strToU8(asset.content)
        : Uint8Array.from(atob(asset.content), (c) => c.charCodeAt(0));
  }
  if (Object.values(files).reduce((n, bytes) => n + bytes.length, 0) > 4_000_000)
    throw Error('Website exceeds 4 MB.');
  return files;
}
export function websiteZip(site: unknown) {
  return zipSync(websiteFiles(site));
}
export function websitePreview(site: Website) {
  const files = websiteFiles(site);
  let html = new TextDecoder().decode(files['index.html']);
  html = html
    .replace(
      '<link rel="stylesheet" href="styles.css">',
      `<style>${site.css.replace(/<\/style/gi, '<\\/style')}</style>`,
    )
    .replace(
      '<script src="script.js"></script>',
      `<script>${site.js.replace(/<\/script/gi, '<\\/script')}</script>`,
    );
  for (const asset of site.assets) {
    const ext = asset.path.split('.').pop();
    const mime =
      ext === 'svg'
        ? 'image/svg+xml'
        : ext === 'woff2'
          ? 'font/woff2'
          : `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    const content =
      asset.encoding === 'base64'
        ? asset.content
        : btoa(
            Array.from(new TextEncoder().encode(asset.content), (c) => String.fromCharCode(c)).join(
              '',
            ),
          );
    html = html.split(asset.path).join(`data:${mime};base64,${content}`);
  }
  const policy = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src data:; connect-src 'none'; form-action 'none'; base-uri 'none'">`;
  return `<!doctype html><html><head>${policy}</head><body>${html}</body></html>`;
}
